import React, { useState, useEffect } from "react";
import { API } from "../../../config/api";
import { get } from "../../../lib/http";
import { useNavigate } from "react-router-dom";

const PaymentStatusWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await get(API.paymentReconciliation.dailyReport, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (res?.success) {
        setStats(res.report);
      }
    } catch (err) {
      console.error("Failed to fetch payment stats:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const needsAttention = stats?.pending > 10;

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            💰 Today's Payments
          </h3>
          {needsAttention && (
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
              ⚠️ Action Needed
            </span>
          )}
        </div>

        <div className="space-y-3">
          {/* Successful */}
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✅</span>
              <span className="text-sm font-medium text-gray-700">Successful</span>
            </div>
            <span className="text-lg font-bold text-green-700">
              {stats?.successful || 0}
            </span>
          </div>

          {/* Failed */}
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-red-600">❌</span>
              <span className="text-sm font-medium text-gray-700">Failed</span>
            </div>
            <span className="text-lg font-bold text-red-700">
              {stats?.failed || 0}
            </span>
          </div>

          {/* Pending */}
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-yellow-600">⏳</span>
              <span className="text-sm font-medium text-gray-700">Pending</span>
            </div>
            <span className="text-lg font-bold text-yellow-700">
              {stats?.pending || 0}
            </span>
          </div>

          {/* Revenue */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">💵</span>
              <span className="text-sm font-medium text-gray-700">Revenue</span>
            </div>
            <span className="text-lg font-bold text-blue-700">
              ₹{stats?.totalRevenue?.toFixed(2) || "0.00"}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={() => navigate("/admin/payment-reconciliation")}
          className="mt-4 w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          View Details →
        </button>

        {needsAttention && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              ⚠️ High number of pending payments. Consider running reconciliation.
            </p>
          </div>
        )}

        <div className="mt-3 text-xs text-gray-500 text-center">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusWidget;
